<template>
  <v-card>
    <v-toolbar
      class="datatable-object settings-window-toolbar"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.VoipDomain.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.VoipDomain.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.VoipDomain.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.VoipDomain.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      class="pa-1"
    >
      <v-card flat>
        <v-card-title class="pa-0">
          <v-toolbar
            class="filter-toolbar"
            flat
          >
            <template #default>
              <v-btn
                v-if="posUserCan('SystemSettingsDomains', 'ADD')"
                :color="$t('Settings.VoipDomain.Button.Add.Color')"
                :icon="$vuetify.breakpoint.xsOnly"
                class="elevation-0 ma-1"
                text
                @click="onAddVoipDomainClick"
              >
                <v-icon :left="$vuetify.breakpoint.smAndUp">
                  {{ $t('Settings.VoipDomain.Button.Add.Icon') }}
                </v-icon>&nbsp;

                <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Settings.VoipDomain.Button.Add.Title').toLocaleUpperCase($i18n.locale) }}</span>
              </v-btn>

              <v-spacer />

              <v-autocomplete
                v-model="dataTable.filter.filter_domain"
                :filter="filterDomain"
                :items="domains"
                :label="$t('Staff.StaffAddDialog.Field.Domain.Label')"
                :placeholder="$t('Staff.StaffAddDialog.Field.Domain.Label')"
                class="mr-1"
                clearable
                flat
                hide-details
                item-text="Domain"
                item-value="Id"
                required
                solo
                style="max-width: 400px"
              >
                <template #selection="data">
                  <div class="text-caption">
                    <div>{{ data.item.Domain }}</div>
                    <div
                      :class="data.item.CNameActive ? 'green--text' : 'grey--text'"
                      class="text-caption"
                    >
                      {{ data.item.CName }}
                    </div>
                  </div>
                </template>

                <template #item="data">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ data.item.Domain }}
                    </v-list-item-title>

                    <v-list-item-subtitle
                      :class="data.item.CNameActive ? 'green--text' : 'grey--text'"
                      class="text-caption"
                    >
                      {{ data.item.CName }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <filter-search v-model="dataTable.search.terms" />
            </template>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-container
            class="pa-0"
            fluid
          >
            <v-row dense>
              <v-col cols="12">
                <v-data-table
                  :expanded.sync="expand"
                  :footer-props="dataTable.footerProps"
                  :group-by.sync="dataTable.options.groupBy"
                  :group-desc.sync="dataTable.options.groupDesc"
                  :headers="dataTableHeaders"
                  :height="dataTable.resize.bodyHeight"
                  :items="dataTable.data"
                  :loading="dataTable.loading"
                  :loading-text="dataTable.text.loadingText"
                  :no-data-text="dataTable.text.noDataText"
                  :no-results-text="dataTable.text.noResultsText"
                  :options.sync="dataTable.options"
                  :search="dataTable.search.terms"
                  :server-items-length="dataTable.total"
                  fixed-header
                  item-key="Id"
                  show-expand
                  single-expand
                >
                  <template #[`item.id`]="{item}">
                    #{{ item.Id }}
                  </template>

                  <template #[`item.voip_id`]="{item}">
                    {{ item.VoipId }}
                  </template>

                  <template #[`item.phone`]="{item}">
                    {{ item.Phone }}
                  </template>

                  <template #[`item.domain`]="{item}">
                    <div v-if="item.Domain">
                      <div v-if="item.Domain.Domain">
                        <v-icon
                          class="mr-1"
                          color="success"
                          small
                        >
                          mdi-check-circle
                        </v-icon>

                        <a
                          :href="`https://${item.Domain.Domain}/`"
                          target="_blank"
                        >{{ item.Domain.Domain }}</a>
                      </div>

                      <div
                        v-if="item.Domain.CName"
                        class="grey--text"
                      >
                        <v-icon
                          :color="item.Domain.CName === item.Domain.DomainLive ? 'success' : 'grey lighten-2'"
                          class="mr-1"
                          small
                        >
                          mdi-check-circle
                        </v-icon>

                        <a
                          :href="`https://${item.Domain.CName}/`"
                          target="_blank"
                        >{{ item.Domain.CName }}</a>
                      </div>
                    </div>
                  </template>

                  <template #[`item.description`]="{item}">
                    {{ item.Description }}
                  </template>

                  <template #[`item.voip_active`]="{item}">
                    <v-switch
                      v-model="item.Domain.VoipActive"
                      :label="item.Domain.VoipActive ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                      @change="updateDomainStatus(item)"
                    />
                  </template>

                  <template #[`item.data-table-expand`]="props">
                    <v-speed-dial
                      v-model="props.item.fab"
                      class="d-inline-block"
                      direction="left"
                      transition="slide-x-reverse-transition"
                    >
                      <template #activator>
                        <v-btn
                          v-model="props.item.fab"
                          class="blue darken-2 elevation-2 ma-0 mr-1"
                          dark
                          fab
                          icon
                          style="width: 32px; height: 32px;"
                        >
                          <v-icon>{{ props.item.fab ? 'close' : 'menu' }}</v-icon>
                        </v-btn>
                      </template>

                      <v-tooltip
                        color="cyan"
                        open-delay="1000"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            v-if="userCanManage && userIsSuperAdmin"
                            class="cyan elevation-2 ma-0 mr-1"
                            dark
                            icon
                            style="width: 32px; height: 32px;"
                            @click="onEditVoipDomainClick(props.item)"
                            v-on="on"
                          >
                            <v-icon>edit</v-icon>
                          </v-btn>
                        </template>

                        <span>Edit Voip Domain</span>
                      </v-tooltip>

                      <v-tooltip
                        color="red"
                        open-delay="1000"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            v-if="userCanManage && userCanDelete && userIsSuperAdmin"
                            class="red elevation-2 ma-0 mr-1"
                            dark
                            icon
                            style="width: 32px; height: 32px;"
                            @click="onDeleteVoipDomainClick(props.item)"
                            v-on="on"
                          >
                            <v-icon>delete</v-icon>
                          </v-btn>
                        </template>

                        <span>Delete Voip Domain</span>
                      </v-tooltip>
                    </v-speed-dial>

                    <v-tooltip
                      color="grey"
                      open-delay="1000"
                      top
                    >
                      <template #activator="{on}">
                        <v-btn
                          class="grey elevation-2 ma-0"
                          dark
                          icon
                          style="width: 32px; height: 32px;"
                          @click="props.expand(!props.isExpanded)"
                          v-on="on"
                        >
                          <v-icon>{{ !props.isExpanded ? 'expand_more' : 'expand_less' }}</v-icon>
                        </v-btn>
                      </template>

                      <span>Expand</span>
                    </v-tooltip>
                  </template>

                  <template #expanded-item="{headers, item}">
                    <td :colspan="headers.length">
                      <div class="divTable my-2">
                        <div class="divTableHeading">
                          <div class="divTableRow">
                            <div class="divTableHead text-center">
                              ID
                            </div>

                            <div class="divTableHead text-left">
                              UID
                            </div>

                            <div class="divTableHead text-center">
                              VOIP EXTENSION (AGENT)
                            </div>

                            <div class="divTableHead text-center">
                              QUEUE
                            </div>

                            <div class="divTableHead text-right text-no-wrap">
                              <v-btn
                                class="primary elevation-2 ma-0 mx-1"
                                dark
                                icon
                                style="width: 32px; height: 32px;"
                                @click="onAddVoipDomainUserClick(item)"
                              >
                                <v-icon>add</v-icon>
                              </v-btn>
                            </div>
                          </div>
                        </div>

                        <div class="divTableBody">
                          <template v-for="user in item.Users">
                            <div
                              :key="`voip-domain-${item.Id}-user-${user.Id}`"
                              class="divTableRow"
                            >
                              <div class="divTableCell text-center">
                                {{ user.Id }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ user.Uid }}
                              </div>

                              <div class="divTableCell text-center">
                                {{ user.VoipAgent }}
                              </div>

                              <div class="divTableCell text-center">
                                {{ user.Queue }}
                              </div>

                              <div class="divTableHead text-right text-no-wrap">
                                <v-btn
                                  class="red elevation-2 ma-0 mx-1"
                                  dark
                                  icon
                                  style="width: 32px; height: 32px;"
                                  @click="onDeleteVoipDomainUserClick(user)"
                                >
                                  <v-icon>delete</v-icon>
                                </v-btn>

                                <v-btn
                                  class="cyan elevation-2 ma-0 mx-1"
                                  dark
                                  icon
                                  style="width: 32px; height: 32px;"
                                  @click="onEditVoipDomainUserClick(user, item)"
                                >
                                  <v-icon>edit</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </td>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 datatable-object settings-window-actions">
      <v-spacer />

      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        to="/system-settings"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>

    <voip-domain-edit-dialog
      :visible.sync="voipDomainEditDialogVisible"
      :voip-domain="selectedVoipDomain"
      @save-success="getData"
    />

    <voip-user-edit-dialog
      :user="selectedVoipUser"
      :visible.sync="voipUserEditDialogVisible"
      :voip-domain="selectedVoipDomain"
      @save-success="getData"
    />

    <confirm-dialog
      :html-content="$t('Settings.VoipDomain.Dialog.Confirm.DeleteDomain.Content', {domain: selectedVoipDomain?.Domain?.DomainLive})"
      :html-title="$t('Settings.VoipDomain.Dialog.Confirm.DeleteDomain.Title')"
      :info-bar="true"
      :info-bar-body="$t('Charge.Dialog.Delete.Notification')"
      :visible.sync="confirmDeleteDomainDialogVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      @confirm-dialog-yes="onDeleteVoipDomainConfirm"
    />

    <confirm-dialog
      :html-content="$t('Settings.VoipDomain.Dialog.Confirm.DeleteUser.Content', {user: selectedVoipUser?.Uid})"
      :html-title="$t('Settings.VoipDomain.Dialog.Confirm.DeleteUser.Title')"
      :info-bar="true"
      :info-bar-body="$t('Charge.Dialog.Delete.Notification')"
      :visible.sync="confirmDeleteUserDialogVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      @confirm-dialog-yes="onDeleteVoipUserConfirm"
    />
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import Watchable from '@/mixins/watchable'
import DataTable from '@/mixins/data-table/dataTable'
import FilterSearch from '@/components/common/filter/FilterSearch'
import SocketCommand from '@/api/SocketCommand'
import VoipUserEditDialog from '@/components/settings/voipDomains/VoipUserEditDialog.vue'
import VoipDomainEditDialog from '@/components/settings/voipDomains/VoipDomainEditDialog.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'

export default {
  name      : 'SystemSettingsVoipDomains',
  components: {
    ConfirmDialog,
    VoipDomainEditDialog,
    VoipUserEditDialog,
    FilterSearch
  },
  mixins: [AppData, Auth, Watchable, DataTable],
  data() {
    return {
      expand                          : [false],
      voipUserEditDialogVisible       : false,
      voipDomainEditDialogVisible     : false,
      confirmDeleteDomainDialogVisible: false,
      confirmDeleteUserDialogVisible  : false,
      selectedVoipUser                : null,
      selectedVoipDomain              : null,
      dataTable                       : {
        resize: {
          offset: 42
        },
        socketCommand: {
          list  : SocketCommand.Settings.VoipDomains.All,
          save  : SocketCommand.Settings.VoipDomains.Save,
          delete: SocketCommand.Settings.VoipDomains.Delete
        },
        filter: {
          filter_domain: null
        },
        options: {
          defaultSortBy    : 'id',
          sortBy           : ['id'],
          sortDesc         : [true],
          itemsPerPage     : 20,
          serverSideEnabled: true
        },
        footerProps: {
          disableItemsPerPage: false,
          itemsPerPageOptions: [5, 10, 15, 20, 25, 30],
          itemsPerPageText   : ''
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : '#',
            value    : 'id',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : 'Voip ID',
            value    : 'voip_id',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : 'Phone',
            value    : 'phone',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : 'Domain',
            value    : 'domain',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : 'Description',
            value    : 'description',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : 'Active',
            value    : 'voip_active',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : '',
            value    : 'data-table-expand',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption',
            width    : '120'
          }
        ]
      }
    }
  },
  computed: {
    domains() {
      return this.$DataStore?.appDomains || []
    }
  },
  watch: {},
  created() {
  },
  mounted() {
    this.$bus.$on(window.SocketCommand.Settings.VoipDomains.Delete, this.onDeleteVoipDomainResult)
    this.$bus.$on(window.SocketCommand.Settings.VoipDomains.User.Delete, this.onDeleteVoipUserResult)
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Settings.VoipDomains.Delete, this.onDeleteVoipDomainResult)
    this.$bus.$off(window.SocketCommand.Settings.VoipDomains.User.Delete, this.onDeleteVoipUserResult)
  },
  methods: {
    onAddVoipDomainClick() {
      this.selectedVoipDomain = null
      this.voipDomainEditDialogVisible = true
    },
    onEditVoipDomainClick(voipDomain) {
      if (!voipDomain) return

      this.selectedVoipDomain = voipDomain
      this.voipDomainEditDialogVisible = true
    },
    onDeleteVoipDomainClick(voipDomain) {
      if (!voipDomain) return

      this.selectedVoipDomain = voipDomain
      this.confirmDeleteDomainDialogVisible = true
    },
    onDeleteVoipDomainConfirm() {
      this.selectedVoipDomain.isLoading = true

      window.callAS(window.SocketCommand.Settings.VoipDomains.Delete, {Id: this.selectedVoipDomain.Id})
    },
    onDeleteVoipDomainResult(data) {
      this.selectedVoipDomain.isLoading = false

      if (data.status === 'success') {
        this.getData()
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.VoipDomain.Notification.DeleteDomain.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.VoipDomain.Notification.DeleteDomain.Error'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onAddVoipDomainUserClick(voipDomain) {
      if (!voipDomain) return

      this.selectedVoipUser = null
      this.selectedVoipDomain = voipDomain
      this.voipUserEditDialogVisible = true
    },
    onEditVoipDomainUserClick(user, voipDomain) {
      if (!user || !voipDomain || voipDomain?.isLoading) return

      this.selectedVoipUser = user
      this.selectedVoipDomain = voipDomain
      this.voipUserEditDialogVisible = true
    },
    onDeleteVoipDomainUserClick(user) {
      if (!user || user?.isLoading) return

      this.selectedVoipUser = user
      this.confirmDeleteUserDialogVisible = true
    },
    onDeleteVoipUserConfirm() {
      this.selectedVoipUser.isLoading = true

      window.callAS(window.SocketCommand.Settings.VoipDomains.User.Delete, {Id: this.selectedVoipUser.Id})
    },
    onDeleteVoipUserResult(data) {
      this.selectedVoipUser.isLoading = false

      if (data.status === 'success') {
        this.getData()
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.VoipDomain.Notification.DeleteUser.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.VoipDomain.Notification.DeleteUser.Error'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    updateDomainStatus(voipDomain) {
      const payload = {
        Id         : voipDomain?.Id || null,
        DomainId   : voipDomain?.DomainId || null,
        VoipId     : voipDomain?.VoipId || '',
        Phone      : voipDomain?.Phone || '',
        Description: voipDomain?.Description || '',
        VoipActive : voipDomain?.Domain?.VoipActive ?? false
      }

      window.callAS(window.SocketCommand.Settings.VoipDomains.Save, payload)
    },

    filterDomain(item, queryText, itemText) {
      const domain = item.Domain?.toLowerCase() || ''
      const cname = item.CName?.toLowerCase() || ''
      const searchText = queryText.toLowerCase()

      return domain.includes(searchText) || cname.includes(searchText)
    }
  }
}
</script>

<style scoped>
:deep(.single-login-label label.v-label) {
  font-size: 12px !important;
  padding-right: 0;
  margin: 0;
}
</style>
